import { useState } from 'react';
import { useApp } from '../../app/AppContext';
import { useStore } from '../../app/StoreContext';
import { callFn } from '../../lib/api';
import { MediaPicker } from '../../components/MediaLibrary';
import type { MediaItem, MenuItem } from '../../lib/types';

interface Draft { category: string; original: string | null; item: string; price: string; description: string; image: string; special: boolean; outOfStock: boolean; }

export function AdminMenu() {
  const { me, reload, toast } = useApp();
  const { reloadStore } = useStore();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [newCat, setNewCat] = useState('');
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState(false);
  const menu = me?.menu || [];

  const refresh = async () => { await reload(); await reloadStore(); };
  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); toast(label, 'ok'); await refresh(); return true; }
    catch (e) { toast(e instanceof Error ? e.message : 'That did not work.', 'err'); return false; }
    finally { setBusy(false); }
  };

  const openNew = (category: string) => setDraft({ category, original: null, item: '', price: '', description: '', image: '', special: false, outOfStock: false });
  const openEdit = (category: string, i: MenuItem) => setDraft({ category, original: i.item, item: i.item, price: i.price || '', description: i.description || '', image: i.image || '', special: !!i.special, outOfStock: !!i.outOfStock });

  const save = async () => {
    if (!draft) return;
    const name = draft.item.trim();
    if (!name) { toast('Give the item a name.', 'err'); return; }
    const fields = { price: draft.price.trim() || null, description: draft.description.trim() || null, image: draft.image.trim() || null, special: draft.special, outOfStock: draft.outOfStock };
    const ok = draft.original === null
      ? await run('Item added', () => callFn('menu_add_item', { category: draft.category, item: { item: name, ...fields } }))
      : await run('Item saved', () => callFn('menu_update_item', { category: draft.category, itemName: draft.original, patch: { ...(name !== draft.original ? { item: name } : {}), ...fields } }));
    if (ok) setDraft(null);
  };
  const remove = async () => {
    if (!draft?.original) return;
    if (await run('Item removed', () => callFn('menu_delete_item', { category: draft.category, itemName: draft.original }))) setDraft(null);
  };
  const addCategory = async () => {
    const c = newCat.trim(); if (!c) return;
    if (await run('Category added', () => callFn('menu_add_category', { category: c }))) setNewCat('');
  };
  const deleteCategory = (c: string, count: number) => {
    if (count > 0 && !window.confirm(`Delete "${c}" and its ${count} item${count === 1 ? '' : 's'}?`)) return;
    void run('Category removed', () => callFn('menu_delete_category', { category: c }));
  };
  const quickToggle = (c: string, i: MenuItem, key: 'special' | 'outOfStock') =>
    run(key === 'special' ? (i.special ? 'Special cleared' : 'Marked special') : (i.outOfStock ? 'Back in stock' : 'Marked sold out'),
      () => callFn('menu_update_item', { category: c, itemName: i.item, patch: { [key]: !i[key] } }));

  return (
    <div className="admin-menu">
      <div className="row between wrap">
        <h1>Menu</h1>
        <form className="row" onSubmit={(e) => { e.preventDefault(); void addCategory(); }}>
          <input className="input" placeholder="New category" value={newCat} onChange={(e) => setNewCat(e.target.value)} />
          <button className="btn btn-sm" disabled={busy || !newCat.trim()} type="submit">Add category</button>
        </form>
      </div>
      {menu.length === 0 && <p className="muted">No categories yet.</p>}
      {menu.map((c) => (
        <section key={c.category} className="menu-cat">
          <div className="row between">
            <h2>{c.category} <span className="muted small">({c.items.length})</span></h2>
            <div className="row">
              <button className="btn btn-sm" disabled={busy} onClick={() => openNew(c.category)}>Add item</button>
              <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => deleteCategory(c.category, c.items.length)} aria-label={`Delete ${c.category}`}>Delete</button>
            </div>
          </div>
          <ul className="items">
            {c.items.map((i) => (
              <li key={i.item} className={`item ${i.outOfStock ? 'item-oos' : ''}`}>
                <button className="item-main" onClick={() => openEdit(c.category, i)}>
                  {i.image && <img className="item-thumb" src={i.image} alt="" loading="lazy" />}
                  <span className="item-text">
                    <span className="item-name">{i.item}{i.special && <span className="tag">Special</span>}{i.outOfStock && <span className="tag tag-muted">Sold out</span>}</span>
                    {i.description && <span className="item-desc">{i.description}</span>}
                  </span>
                </button>
                <div className="item-side">
                  <span className="price">{i.price || '—'}</span>
                  <div className="row">
                    <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => void quickToggle(c.category, i, 'special')} title="Toggle special">★</button>
                    <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => void quickToggle(c.category, i, 'outOfStock')} title="Toggle sold out">{i.outOfStock ? '↺' : '⊘'}</button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {draft && (
        <div className="sheet-backdrop" onClick={() => setDraft(null)}>
          <div className="sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="sheet-body">
              <span className="muted small">{draft.category}</span>
              <h3>{draft.original === null ? 'New item' : 'Edit item'}</h3>
              <label>Name<input className="input" value={draft.item} onChange={(e) => setDraft({ ...draft, item: e.target.value })} /></label>
              <label>Price (like $12.50)<input className="input" inputMode="decimal" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} /></label>
              <label>Description<textarea className="input" rows={3} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></label>
              <div className="row wrap" style={{ gap: 12 }}>
                <label className="check"><input type="checkbox" checked={draft.special} onChange={(e) => setDraft({ ...draft, special: e.target.checked })} /> Special</label>
                <label className="check"><input type="checkbox" checked={draft.outOfStock} onChange={(e) => setDraft({ ...draft, outOfStock: e.target.checked })} /> Sold out</label>
              </div>
              <div className="photo-row">
                {draft.image ? <img className="item-thumb big" src={draft.image} alt="" /> : <span className="item-thumb big placeholder" aria-hidden />}
                <div className="row wrap" style={{ gap: 8 }}>
                  <button className="btn btn-sm" onClick={() => setPick(true)}>Choose from media library</button>
                  {draft.image && <button className="btn btn-sm btn-ghost" onClick={() => setDraft({ ...draft, image: '' })}>Remove photo</button>}
                </div>
              </div>
              <div className="row between" style={{ marginTop: 16 }}>
                {draft.original !== null ? <button className="btn btn-danger btn-sm" disabled={busy} onClick={() => void remove()}>Delete item</button> : <span />}
                <div className="row">
                  <button className="btn btn-ghost" onClick={() => setDraft(null)}>Cancel</button>
                  <button className="btn" disabled={busy} onClick={() => void save()}>{busy ? 'Saving…' : 'Save'}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {pick && draft && <MediaPicker onClose={() => setPick(false)} onPick={(m: MediaItem) => { setDraft({ ...draft, image: m.url }); setPick(false); }} />}
    </div>
  );
}
